/**
 * CoachNotesSection - Component to display coach notes in FitTrack
 * Shows notes received from coaches to the user
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  Pin,
  Check,
  ChevronDown,
  ChevronUp,
  Dumbbell,
  Bell,
} from 'lucide-react';
import { useCoachStore, NOTE_TYPES } from '../../stores/coachStore';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';

const CoachNotesSection = () => {
  const {
    myNotes,
    isLoadingNotes,
    loadMyNotes,
    markNoteAsRead,
    markAllNotesAsRead,
    unreadNotesCount,
    myCoaches,
  } = useCoachStore();

  const [isExpanded, setIsExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    loadMyNotes();
  }, [loadMyNotes]);

  // Don't show if no coaches
  if (myCoaches.length === 0 && myNotes.length === 0) {
    return null;
  }

  // Filter pinned and recent notes
  const pinnedNotes = myNotes.filter((n) => n.is_pinned);
  const recentNotes = myNotes.filter((n) => !n.is_pinned);
  const displayedNotes = showAll ? recentNotes : recentNotes.slice(0, 3);
  const hasMore = recentNotes.length > 3;

  const handleMarkAsRead = async (noteId) => {
    try {
      await markNoteAsRead(noteId);
    } catch (error) {
      // Ignore
    }
  };

  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-800/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-orange-400" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold flex items-center gap-2">
              Koç Notları
              {unreadNotesCount > 0 && (
                <span className="px-1.5 py-0.5 bg-orange-500 text-white text-xs rounded-full">
                  {unreadNotesCount} yeni
                </span>
              )}
            </h3>
            <p className="text-sm text-gray-400">
              {myNotes.length > 0
                ? `${myNotes.length} not`
                : 'Koçlarınızdan gelen notlar'}
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 space-y-4">
              {/* Mark all as read button */}
              {unreadNotesCount > 0 && (
                <button
                  onClick={() => markAllNotesAsRead()}
                  className="text-sm text-orange-400 hover:text-orange-300 flex items-center gap-1"
                >
                  <Check className="w-4 h-4" />
                  Tümünü okundu işaretle
                </button>
              )}

              {/* Empty state */}
              {myNotes.length === 0 ? (
                <div className="text-center py-6">
                  <Bell className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">
                    Henüz koçlarınızdan not almadınız
                  </p>
                </div>
              ) : (
                <>
                  {/* Pinned Notes */}
                  {pinnedNotes.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1">
                        <Pin className="w-3 h-3" />
                        Sabitlenmiş
                      </p>
                      {pinnedNotes.map((note) => (
                        <NoteCard
                          key={note.id}
                          note={note}
                          onMarkAsRead={handleMarkAsRead}
                        />
                      ))}
                    </div>
                  )}

                  {/* Recent Notes */}
                  {recentNotes.length > 0 && (
                    <div className="space-y-2">
                      {pinnedNotes.length > 0 && (
                        <p className="text-xs text-gray-500 uppercase tracking-wider">
                          Son Notlar
                        </p>
                      )}
                      {displayedNotes.map((note) => (
                        <NoteCard
                          key={note.id}
                          note={note}
                          onMarkAsRead={handleMarkAsRead}
                        />
                      ))}

                      {/* Show more button */}
                      {hasMore && (
                        <button
                          onClick={() => setShowAll(!showAll)}
                          className="w-full py-2 text-sm text-gray-400 hover:text-white transition-colors"
                        >
                          {showAll
                            ? 'Daha az göster'
                            : `${recentNotes.length - 3} not daha göster`}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Note Card Component
const NoteCard = ({ note, onMarkAsRead }) => {
  const noteType = NOTE_TYPES[note.note_type] || NOTE_TYPES.feedback;
  const isUnread = !note.is_read;

  const handleClick = () => {
    if (isUnread) {
      onMarkAsRead(note.id);
    }
  };

  return (
    <motion.div
      onClick={handleClick}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-lg border transition-colors cursor-pointer ${
        isUnread
          ? 'bg-orange-500/5 border-orange-500/30 hover:bg-orange-500/10'
          : 'bg-gray-800/50 border-gray-700 hover:bg-gray-800'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Coach Avatar */}
        <div className="w-8 h-8 bg-orange-500/10 rounded-full flex items-center justify-center flex-shrink-0">
          <Dumbbell className="w-4 h-4 text-orange-400" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm">
              {note.coach?.display_name || 'Koç'}
            </span>
            <span className="text-lg">{noteType.emoji}</span>
            {note.is_pinned && (
              <Pin className="w-3 h-3 text-orange-400" />
            )}
            {isUnread && (
              <span className="w-2 h-2 bg-orange-500 rounded-full" />
            )}
          </div>

          <p className="text-sm text-gray-300 whitespace-pre-wrap line-clamp-3">
            {note.content}
          </p>

          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            {note.workout_date && (
              <span>
                {format(parseISO(note.workout_date), 'd MMM', { locale: tr })} antrenmanı
              </span>
            )}
            <span>
              {formatDistanceToNow(parseISO(note.created_at), {
                addSuffix: true,
                locale: tr,
              })}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CoachNotesSection;
